import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, provide } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';

import {
  NavBar,
  HelperViewStatus,
  LoaderIndicator
} from '@warrenbrasil/nebraska-web';
import { FadeTransition } from '@/modules/common/components/FadeTransition';
import { BackButton } from '@/modules/common/components/buttons/BackButton';
import {
  getWithdrawDetails,
  IWithdrawDetails,
  getWithdrawSummary,
  IWithdrawSummary,
  sendWithdrawSuggested,
  ISendWithdrawSuggested
} from '@/modules/transaction/services/withdraw-by-portfolio';
import { getBankList, IBankList } from '@/modules/onboarding/services';

import {
  EWithdrawByPortfolioSteps,
  WithdrawByPortfolioComponents
} from './helpers';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';


export default /*#__PURE__*/_defineComponent({
  __name: 'WithdrawByPortfolio',
  setup(__props) {

const route = useRoute();
const router = useRouter();

const currentStep = ref(EWithdrawByPortfolioSteps.WITHDRAW);

const withdrawDetails = ref<IWithdrawDetails | null>(null);
const withdrawReview = ref<IWithdrawSummary | null>(null);
const bankList = ref<IBankList | null>(null);
const selectedBank = ref('');

const isLoading = ref(true);
const hasError = ref(false);
const amountValue = ref(0);

const portfolioId = computed(() => {
  return route.params.id as string;
});

const step = computed(() => {
  return {
    component: WithdrawByPortfolioComponents[currentStep.value]
  };
});

const showNavbar = computed(() => {
  return currentStep.value !== EWithdrawByPortfolioSteps.CONCLUSION;
});

const hasBankAccounts = computed(() => {
  const accounts = bankList.value?.externalBankAccounts?.bankAccounts;

  return Array.isArray(accounts) && accounts.length > 0;
});

function backStep() {
  if (currentStep.value === EWithdrawByPortfolioSteps.WITHDRAW) {
    router.back();
    return;
  }

  if (
    currentStep.value === EWithdrawByPortfolioSteps.REVIEW &&
    (!withdrawDetails.value?.canRedeemDirectly || !hasBankAccounts.value)
  ) {
    return changeStep(EWithdrawByPortfolioSteps.WITHDRAW);
  }

  currentStep.value = currentStep.value - 1;
}

function changeStep(step: EWithdrawByPortfolioSteps) {
  currentStep.value = step;
}

async function fetchWithdrawReview() {
  try {
    withdrawReview.value = await getWithdrawSummary(
      portfolioId.value,
      amountValue.value,
      selectedBank.value
    );

    changeStep(EWithdrawByPortfolioSteps.REVIEW);
  } catch {
    notify({
      group: 'wrn-notifications',
      type: NotificationTypes.error,
      text: 'Não foi possível carregar os dados. Tente novamente mais tarde.'
    });
  }
}

async function fetchBankList() {
  try {
    bankList.value = await getBankList();

    if (!hasBankAccounts.value) {
      await fetchWithdrawReview();

      return;
    }

    changeStep(EWithdrawByPortfolioSteps.BANK);
  } catch {
    notify({
      group: 'wrn-notifications',
      type: NotificationTypes.error,
      text: 'Não foi possível carregar a lista de bancos. Tente novamente mais tarde.'
    });
  }
}

async function sendWithdraw(partnerId?: string) {
  try {
    const payload: ISendWithdrawSuggested = {
      portfolioId: portfolioId.value,
      amount: amountValue.value,
      bankId: selectedBank.value,
      ...(partnerId && { partnerId })
    };

    await sendWithdrawSuggested(payload);

    changeStep(EWithdrawByPortfolioSteps.CONCLUSION);
  } catch (error) {
    notify({
      group: 'wrn-notifications',
      type: NotificationTypes.error,
      text: 'Erro ao enviar o resgate.'
    });
  }
}

async function getPortfolioToWithdraw() {
  try {
    hasError.value = false;
    isLoading.value = true;

    withdrawDetails.value = await getWithdrawDetails(portfolioId.value);
  } catch (error) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

onMounted(async () => {
  await getPortfolioToWithdraw();
});

provide('withdrawDetails', withdrawDetails);
provide('withdrawReview', withdrawReview);
provide('bankList', bankList);
provide('fetchBankList', fetchBankList);
provide('fetchWithdrawReview', fetchWithdrawReview);
provide('sendWithdraw', sendWithdraw);

provide('updateAmount', (value: number) => {
  amountValue.value = value;
});
provide('updateBank', (value: string) => {
  selectedBank.value = value;
});

return { __sfc: true,route, router, currentStep, withdrawDetails, withdrawReview, bankList, selectedBank, isLoading, hasError, amountValue, portfolioId, step, showNavbar, hasBankAccounts, backStep, changeStep, fetchWithdrawReview, fetchBankList, sendWithdraw, getPortfolioToWithdraw, NavBar, HelperViewStatus, LoaderIndicator, FadeTransition, BackButton }
}

})